/* Subscription pricing and quantity changes shared by both cart surfaces and PDPs. */
(function () {
  'use strict';

  var plans = window.FuseSubscriptionPlans;
  var legacyPlans = { 7798718714: 1, 10095460602: 2, 10095493370: 3 };
  var handles = ['fuse-dream', 'fuse-boost', 'fuse-lush', 'fuse-shine', 'fuse-solid', 'fuse-vital'];
  var pending = Promise.resolve();
  var operations = 0;

  function planId(item) {
    var allocation = item.selling_plan_allocation;
    return allocation && allocation.selling_plan ? Number(allocation.selling_plan.id) : null;
  }

  function cadence(item) {
    if (handles.indexOf(item.handle) === -1) return null;
    var id = planId(item);
    if (legacyPlans[id]) return legacyPlans[id];
    return [1, 2, 3].find(function (months) {
      return plans[months].indexOf(id) !== -1;
    }) || null;
  }

  function identity(item, includePlan) {
    var properties = item.properties || {};
    return JSON.stringify([
      item.variant_id,
      Object.keys(properties).sort().map(function (key) { return [key, properties[key]]; }),
      includePlan ? planId(item) : null
    ]);
  }

  function withTimeout(operation) {
    var controller = new AbortController();
    var timer;
    var deadline = new Promise(function (_, reject) {
      timer = window.setTimeout(function () {
        reject(new Error('Your cart is taking too long to respond. Check your cart before trying again.'));
        controller.abort();
      }, 15000);
    });
    return Promise.race([operation(controller.signal), deadline]).finally(function () {
      window.clearTimeout(timer);
    });
  }

  function request(path, body) {
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    var options = { headers: { Accept: 'application/json' } };
    if (body) {
      options.method = 'POST';
      options.headers['Content-Type'] = 'application/json';
      options.body = JSON.stringify(body);
    }
    return withTimeout(async function (signal) {
      options.signal = signal;
      var response = await fetch(root + path, options);
      if (!response.ok) throw new Error('Could not update your cart. Refresh the cart and try again.');
      return response.json();
    });
  }

  // Serialize adds, quantity edits and price refreshes so an older response cannot
  // overwrite newer quantities. Each operation starts from Shopify's current cart.
  function run(operation) {
    operations++;
    var result = pending.then(operation);
    pending = result.catch(function () {});
    return result.finally(function () { operations--; });
  }

  function subscriptionQuantity(cart) {
    return cart.items.reduce(function (total, item) {
      return total + (cadence(item) ? item.quantity : 0);
    }, 0);
  }

  async function currentCart() {
    var cart = await request('cart.js');
    if (window.WISP_REFRESH_LEGACY_CART) {
      cart = await window.WISP_REFRESH_LEGACY_CART(cart, function () { return true; });
    }
    return cart;
  }

  async function reconcile(cart) {
    var tier = Math.min(subscriptionQuantity(cart), 3);
    if (!tier) return cart;
    var limit = cart.items.length;
    for (var i = 0; i <= limit; i++) {
      var item = cart.items.find(function (line) {
        var months = cadence(line);
        return months && planId(line) !== plans[months][tier - 1];
      });
      if (!item) return cart;
      if (i === limit) throw new Error('Could not confirm subscription pricing. Refresh your cart before checking out.');
      // Shopify may merge lines and regenerate every key. Resolve the next line
      // from the returned cart; preserve its cadence, properties and quantity.
      cart = await request('cart/change.js', {
        line: cart.items.indexOf(item) + 1,
        quantity: item.quantity,
        selling_plan: plans[cadence(item)][tier - 1]
      });
    }
  }

  function normalize() {
    return run(async function () { return reconcile(await currentCart()); });
  }

  function update(changes) {
    return run(async function () {
      var cart = await currentCart();
      var seen = {};
      var edits = changes.map(function (change) {
        var quantity = Number(change.quantity);
        if (String(change.quantity).trim() === '' || !Number.isInteger(quantity) || quantity < 0 || seen[change.key]) throw new Error('Enter a valid whole-number quantity.');
        seen[change.key] = true;
        var item = cart.items.find(function (line) { return line.key === change.key; });
        if (!item) throw new Error('Your cart changed. Refresh the cart before updating it.');
        return { item: item, quantity: quantity, months: cadence(item) };
      }).filter(function (edit) { return edit.quantity !== edit.item.quantity; });
      var total = subscriptionQuantity(cart);
      edits.forEach(function (edit) { if (edit.months) total += edit.quantity - edit.item.quantity; });
      var tier = Math.min(total, 3);
      edits.forEach(function (edit) {
        if (!edit.months || !edit.quantity) return;
        var months = edit.quantity <= 3 ? edit.quantity : edit.months;
        edit.plan = plans[months][tier - 1];
        if (edit.plan === planId(edit.item)) return;
        if (edits.some(function (other) {
          return other !== edit && planId(other.item) && identity(other.item, false) === identity(edit.item, false);
        })) throw new Error('Update one subscription at a time when changing delivery schedules for the same product.');
      });
      for (var i = 0; i < edits.length; i++) {
        var edit = edits[i];
        var matches = cart.items.filter(function (item) { return identity(item, true) === identity(edit.item, true); });
        if (matches.length !== 1) throw new Error('Your cart changed. Refresh the cart before updating it.');
        var payload = { line: cart.items.indexOf(matches[0]) + 1, quantity: edit.quantity };
        if (edit.plan && edit.plan !== planId(edit.item)) payload.selling_plan = edit.plan;
        cart = await request('cart/change.js', payload);
      }
      return reconcile(cart);
    });
  }

  function add(url, formData) {
    return run(async function () {
      var response;
      try {
        response = await withTimeout(async function (signal) {
          var result = await fetch(url, { method: 'POST', headers: { Accept: 'application/json' }, body: formData, signal: signal });
          if (!result.ok) {
            var details = {};
            try { details = await result.json(); } catch (parseError) {}
            result.cartDescription = details.description || details.message;
          }
          return result;
        });
      } catch (error) {
        error.transportError = true;
        throw error;
      }
      if (!response.ok) {
        var error = new Error(response.cartDescription || 'Could not add this item. Check your selection and try again.');
        error.cartError = true;
        throw error;
      }
      try {
        return await reconcile(await currentCart());
      } catch (error) {
        error.addConfirmed = true;
        throw error;
      }
    });
  }

  var pageSubmitting = false;
  var form = document.querySelector('[data-cart-page-form]');
  if (form) form.addEventListener('submit', function (event) {
    event.preventDefault();
    if (operations || pageSubmitting) return;
    var checkout = event.submitter && event.submitter.name === 'checkout';
    var inputs = Array.prototype.slice.call(form.querySelectorAll('[data-cart-line-key]'));
    var changes = inputs.filter(function (input) {
      return input.value !== input.getAttribute('data-original-quantity');
    }).map(function (input) { return { key: input.getAttribute('data-cart-line-key'), quantity: input.value }; });
    var errorMessage = form.querySelector('[data-cart-page-error]');
    if (errorMessage) errorMessage.hidden = true;
    var controls = Array.prototype.slice.call(form.querySelectorAll('input, button'));
    pageSubmitting = true;
    form.setAttribute('aria-busy', 'true');
    controls.forEach(function (control) { control.disabled = true; });
    // Even unchanged carts need current native prices before entering checkout.
    update(changes).then(function () {
      if (checkout) window.location.assign(form.getAttribute('data-checkout-url'));
      else window.location.reload();
    }).catch(function (error) {
      if (errorMessage) { errorMessage.textContent = error.message; errorMessage.hidden = false; }
      controls.forEach(function (control) { control.disabled = false; });
      form.removeAttribute('aria-busy');
      pageSubmitting = false;
    });
  });

  window.FuseCartQuantity = { update: update, normalize: normalize, add: add, isBusy: function () { return operations > 0 || pageSubmitting; } };
  // A native Remove link or an old saved cart can change its tier on page load.
  if (form) {
    var original = Array.prototype.map.call(form.querySelectorAll('[data-cart-line-key]'), function (input) {
      return [input.getAttribute('data-cart-line-key'), Number(input.getAttribute('data-original-quantity')), Number(input.getAttribute('data-original-price'))];
    });
    normalize().then(function (cart) {
      var current = cart.items.map(function (item) { return [item.key, item.quantity, item.final_line_price]; });
      if (JSON.stringify(original) !== JSON.stringify(current)) window.location.reload();
    }).catch(function (error) {
      var message = form.querySelector('[data-cart-page-error]');
      if (message) { message.textContent = error.message; message.hidden = false; }
    });
  }
})();
